import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'ip_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true); // true on first load while we verify the token

  // ── Restore session on page load ────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then(({ data }) => {
        setUser(data.user);
        setSubscriptions(data.subscriptions || []);
      })
      .catch(() => {
        // The api interceptor already clears the token when it's genuinely
        // expired (401 + local expiry confirmed). For transient network errors
        // we intentionally do NOT remove the token so the session survives
        // a brief server restart or connectivity blip.
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Register ────────────────────────────────────────────────────────────────
  const register = useCallback(async (full_name, email, password, phone) => {
    const { data } = await api.post('/auth/register', { full_name, email, password, ...(phone ? { phone } : {}) });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions([]);
    return data;
  }, []);

  // ── Login ───────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  // ── Google OAuth ────────────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (_) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setSubscriptions([]);
  }, []);

  // ── Subscription helpers ────────────────────────────────────────────────────
  const hasActiveSubscription = useCallback(
    (roleId) => subscriptions.some((s) => s.job_role_id === roleId && s.status === 'active'),
    [subscriptions]
  );

  const refreshSubscriptions = useCallback(async () => {
    if (!user) return;
    const { data } = await api.get('/auth/me');
    setSubscriptions(data.subscriptions || []);
  }, [user]);

  const value = {
    user,
    subscriptions,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    loginWithGoogle,
    logout,
    hasActiveSubscription,
    refreshSubscriptions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
